import React from 'react';
import { APITypes, Reports } from '../../../../../primary-record-types';
import { getIntl } from '../../../../../utils/intl';
import { Linking } from 'react-native';
import { useHasPermission } from '../../../../v2BaseComponents/Permissions/hooks';
import { reportTypeMessages } from '../../../../../utils/sharedMessages';
import { Paper } from '../../../../v2BaseComponents/Paper';
import Stack, { ContentStack, contentStackSpacing } from '../../../../v2BaseComponents/Stack';
import Text from '../../../../v2BaseComponents/Text';
import MainContentContainer from '../../../../v2BaseComponents/MainContentContainer';
import { useLogarithmicApproachProgress } from '../../../../../hooks/useLogarithmicApproachProgress';
import ProgressBar from '../../../../v2BaseComponents/ProgressBar';
import Button from '../../../../v2BaseComponents/Button';
import { useProfilesTabNavigation } from '../../../../../utils/appNavigationNames';

const intl = getIntl('components.screens.tabs.profiles.reportsScreen.reportCard', {
    'active-medications-description':
        'List of active medications with the dosage instructions, prescriber, source, dates started and last filled.',
    'all-medications-description':
        'List of all medications with the dosage instructions, prescriber, source, dates started and last filled.',
    'general-use': 'General Use',
    pendingStatus: 'Generating report...',
    completeStatus: 'Report generated',
    'summary-report-description':
        'Summary Report of active medications, active allergies, conditions, and visits with the provider and notes.',
    generatingPretext: 'Generating Your {reportName}',
    generatingTimeDescription:
        'An up-to-date PDF of your health data is being created and should be ready for you to view in less than 30 seconds.',
    completeText: 'Your {reportName} is Ready',
    completeSubtext: 'The report will open in a new window that you can view, print or share.',
    viewReport: 'View Report',
    create: 'Create',
    cancelAndRecreate: 'Cancel and re-create',
    viewSharedReport: 'Link/QR Code',
    createSharedReport: 'Create Link/QR Code',
});

export type ReportCardOwnProps = {
    reportType: Reports.ReportType;
    report: APITypes.reportService.ReportItem | null;
    onClick: () => void;
    isDisabled: boolean;
    testID?: string;
    profileId: string;
    sharedReport: APITypes.reportService.SharedReportItem | null;
};

export type ReportCardProps = ReportCardOwnProps;

const ReportCard: React.FC<ReportCardProps> = (props) => {
    const { navigate } = useProfilesTabNavigation();
    const hasEditPermission = useHasPermission(props.profileId, 'reports', 'edit');
    const hasViewPermission = useHasPermission(props.profileId, 'reports', 'view');
    const isComplete = props.report?.status === 'complete';
    const isPending = props.report?.status === 'pending';
    const isViewBlocked = !hasViewPermission;
    const generationProgress = useLogarithmicApproachProgress({
        approximateTimeSeconds: 30,
        isDisabled: !isPending,
    });

    const renderInContainer = (children: React.ReactNode) => (
        <Paper style={{ width: '100%' }} testID={props.testID}>
            <MainContentContainer>
                <ContentStack>{children}</ContentStack>
            </MainContentContainer>
        </Paper>
    );

    const renderSharedReportButton = () => {
        if (props.sharedReport && !isViewBlocked) {
            return (
                <Button
                    style={{ flex: 1 }}
                    title={intl('viewSharedReport')}
                    size="lg"
                    onPress={() => {
                        navigate('shared-report', {
                            profileId: props.profileId,
                            sharedReportId: props.sharedReport!.sharedReportId,
                        });
                    }}
                    testID={`shared-report-open-button-${props.reportType}`}
                    variant="secondary"
                    icon="Share2"
                />
            );
        } else if (!props.sharedReport && hasEditPermission) {
            return (
                <Button
                    style={{ flex: 1 }}
                    title={intl('createSharedReport')}
                    size="lg"
                    onPress={() => {
                        navigate('add-shared-report', { profileId: props.profileId, reportType: props.reportType });
                    }}
                    testID={`shared-report-create-button-${props.reportType}`}
                    variant="secondary"
                    icon="Share2"
                />
            );
        } else {
            return null;
        }
    };

    if (isComplete) {
        return renderInContainer(
            <>
                <Text variant="h4">{intl('completeText', { reportName: reportTypeMessages(props.reportType) })}</Text>
                <Text variant="body-1">{intl(`${props.reportType}-description` as any)}</Text>
                <Stack vertical fullWidth space={contentStackSpacing}>
                    {isViewBlocked ? null : (
                        <Button
                            style={{ flex: 1 }}
                            title={intl('viewReport')}
                            size="lg"
                            onPress={() => {
                                if (props.report?.status === 'complete') {
                                    Linking.openURL(props.report.downloadUrl);
                                }
                            }}
                            icon="ExternalLink"
                            testID="report-open-button"
                        />
                    )}
                    {isViewBlocked ? null : (
                        <Button
                            style={{ flex: 1 }}
                            title={intl('cancelAndRecreate')}
                            size="lg"
                            onPress={props.onClick}
                            variant="secondary"
                            icon="RefreshCw"
                            testID={`${props.testID}-cancel-and-recreate`}
                            isDisabled={props.isDisabled}
                        />
                    )}
                    {renderSharedReportButton()}
                </Stack>
            </>,
        );
    } else if (isPending) {
        return renderInContainer(
            <>
                <Text variant="h4">
                    {intl('generatingPretext', { reportName: reportTypeMessages(props.reportType) })}
                </Text>
                <ProgressBar progress={generationProgress.progress} />
                <Text variant="body-1">{intl('generatingTimeDescription')}</Text>
            </>,
        );
    } else {
        return renderInContainer(
            <>
                <Text variant="h4">{reportTypeMessages(props.reportType)}</Text>
                <Text variant="body-1">{intl(`${props.reportType}-description` as any)}</Text>
                <Stack vertical fullWidth space={contentStackSpacing}>
                    {isViewBlocked ? null : (
                        <Button
                            style={{ flex: 1 }}
                            title={intl('create')}
                            size="lg"
                            onPress={props.onClick}
                            variant="secondary"
                            icon="FileText"
                            testID={`${props.testID}-create`}
                        />
                    )}
                    {renderSharedReportButton()}
                </Stack>
            </>,
        );
    }
};

export default React.memo(ReportCard);
